import { ReactElement, useMemo } from 'react';
import { formatISO, startOfYear, endOfYear } from 'date-fns';
import { VisibilityProps, Visibility } from '../common';
import { usePeriod } from '../usePeriod';
import { columns } from './columns';
import type { Row } from './types';
import { CompanyFilterChip } from 'modules/core/company/components';
import { ReportTable } from 'components/ReportTable';
import { TableColumns } from '@ff-it/ui';
import { OutletOrServiceCategoryFilterChip } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryFilterChip';
import { EnterpriseFilterChip } from 'components/filters/EnterpriseFilterChip';
import { ReportingGroupFilterChip } from '../common/ReportingGroupFilterChip';
import { MonthRangeFilterChip } from 'components/filters/MonthRangeFilterChip';
import { FilterSeparator } from 'components/filters/FilterSeparator';
import { StateFilterChip } from '../StateFilterChip';
import { TypeFilterChip } from '../TypeFilterChip';

const resolveColumns = (visibility: Visibility): TableColumns<Row> => {
  switch (visibility) {
    case 'GLOBAL':
      return columns;
    case 'LOCAL':
      // no agency
      return columns.slice(1);
    case 'LOCAL_MANAGED':
    case 'LOCAL_TYPE':
      // no agency, no bonuses
      return columns.slice(1, 10);
  }
};

export function Client({ visibility, ...props }: VisibilityProps): ReactElement {
  const { date_from: minDate, date_to: maxDate } = usePeriod();
  const initialFilter = {
    date_from: formatISO(startOfYear(new Date()), { representation: 'date' }),
    date_to: formatISO(endOfYear(new Date()), { representation: 'date' }),
  };

  const url = `reporting/client/${visibility.toLowerCase()}${'type' in props ? `/${props.type}/` : '/'}`;

  const columns = useMemo(() => {
    return resolveColumns(visibility);
  }, [visibility]);

  return (
    <ReportTable<Row>
      url={url}
      columns={columns}
      defaultFilter={initialFilter}
      filterFields={
        <>
          <MonthRangeFilterChip
            label="Period"
            fromName="date_from"
            toName="date_to"
            minDate={minDate}
            maxDate={maxDate}
            defaultFrom={initialFilter.date_from}
            defaultTo={initialFilter.date_to}
          />
          <ReportingGroupFilterChip
            name="client_groups"
            label="Client group"
            isMulti
            filter={{ direction: 'CLIENT' }}
          />
          <CompanyFilterChip name="clients" label="Client" isMulti />
          <CompanyFilterChip name="payers" isMulti />
          <CompanyFilterChip name="providers" label="Provider" isMulti />
          <OutletOrServiceCategoryFilterChip
            name="outlets_or_service_categories"
            label="Outlet / service cat."
            isMulti
          />
          {visibility === 'GLOBAL' && <EnterpriseFilterChip name="agency" isMulti defaultValueLabel="All agencies" />}
          <FilterSeparator />
          <StateFilterChip />
          {visibility !== 'LOCAL_TYPE' && <TypeFilterChip global={visibility === 'GLOBAL'} />}
        </>
      }
    />
  );
}
